import type { CityConfig } from "../city-template/types";

export const torranceConfig: CityConfig = {
  city: "Torrance",
  state: "California",
  stateAbbr: "CA",
  slug: "torrance",
  route: "/torrance",
  title: "Emergency Passport Torrance, CA — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Torrance, CA — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for South Bay and Los Angeles County travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Torrance, CA",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Torrance and South Bay travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Torrance%20California%20coastal%20cityscape%20at%20golden%20hour%20with%20modern%20suburban%20buildings%2C%20palm-lined%20streets%2C%20Del%20Amo%20Fashion%20Center%20visible%2C%20clean%20beach%20town%20atmosphere%20with%20warm%20amber%20sunset%20light%2C%20professional%20cityscape%20photography%2C%20wide%20panoramic%20view%2C%20no%20visible%20text%20or%20people%2C%20serene%20and%20modern%20Southern%20California%20environment&width=1800&height=700&seq=torrance-hero-001&orientation=landscape",
  heroImageAlt: "Torrance California coastal cityscape at golden hour with palm-lined streets and modern suburban buildings",
  geoPosition: "33.8358;-118.3406",
  geoPlacename: "Torrance, California",
  lastModified: "2026-07-21",
  regionalAgency: {
    name: "Los Angeles Passport Agency",
    address: "11000 Wilshire Blvd Suite 1000, Los Angeles, CA 90024",
    distance: "Located in West LA — approximately 25–35 minutes from Torrance via I-405",
  },
  nearbyCitiesZips: [
    { name: "Torrance", zips: "90501–90505, 90510" },
    { name: "Redondo Beach", zips: "90277, 90278" },
    { name: "Hermosa Beach", zips: "90254" },
    { name: "Manhattan Beach", zips: "90266, 90267" },
    { name: "Palos Verdes", zips: "90274, 90275" },
    { name: "Carson", zips: "90745, 90746, 90747" },
    { name: "Lakewood", zips: "90712, 90713, 90714" },
    { name: "Long Beach", zips: "90802, 90804, 90805" },
    { name: "Gardena", zips: "90247, 90248, 90249" },
    { name: "Hawthorne", zips: "90250, 90251" },
    { name: "Lawndale", zips: "90260, 90261" },
    { name: "El Segundo", zips: "90245, 90246" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent South Bay departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Los Angeles", route: "/los-angeles" },
    { name: "Long Beach", route: "/long-beach" },
    { name: "Santa Monica", route: "/santa-monica" },
    { name: "Huntington Beach", route: "/huntington-beach" },
    { name: "Irvine", route: "/irvine" },
    { name: "Pasadena", route: "/pasadena" },
    { name: "Anaheim", route: "/anaheim" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Torrance remotely via secure courier. We do not have a walk-in office in Torrance — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for South Bay travelers.",
  fpcOfficeAddress: "601 W Ocean Blvd, Long Beach, CA 90802",
  fpcOfficeDistance: "Serving all of South Bay Los Angeles",
  serviceAreaIntro: "From Old Torrance to the Hollywood Riviera, from the Del Amo Fashion Center to the Wilson Park area — Fast Passport Center serves the entire Torrance community. We regularly help travelers from Redondo Beach, Hermosa Beach, Manhattan Beach, and Palos Verdes get their passports fast.",
  nearbyCitiesIntro: "We serve all Torrance neighborhoods and nearby South Bay cities including Redondo Beach, Hermosa Beach, Manhattan Beach, Palos Verdes, Carson, and Long Beach. If you are in the South Bay area, we have you covered.",
  howItWorksIntro: "Most Torrance travelers complete everything online and ship documents overnight. No need to drive downtown or wait in line at the regional agency.",
  mistakesIntro: "With LAX just 12 miles away, Torrance travelers can't afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "Torrance and South Bay travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Torrance? Here are the most common ones we hear from South Bay travelers.",
  statePageRoute: "/states/california",
  statePageName: "California",
  dmvWebsite: "https://www.dmv.ca.gov",
  dmvLabel: "California DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent South Bay international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Daniel H.",
      location: "Torrance, CA",
      rating: 5,
      text: "My flight to Hong Kong was in 2 days and my passport had expired. Fast Passport Center got me a new one in 30 hours. Absolutely incredible service.",
      service: "Emergency Passport",
    },
    {
      name: "Patricia M.",
      location: "Redondo Beach, CA",
      rating: 5,
      text: "Needed to renew for a cruise out of Long Beach. The process was smooth, they reviewed my documents, and I had my passport in 4 days. Highly recommend.",
      service: "Passport Renewal",
    },
    {
      name: "Steven W.",
      location: "Manhattan Beach, CA",
      rating: 5,
      text: "Lost my passport the week before my wedding in Cabo. They walked me through the DS-64 process and got me a replacement in 48 hours. Saved everything.",
      service: "Lost Passport",
    },
    {
      name: "Linda K.",
      location: "Palos Verdes, CA",
      rating: 5,
      text: "As a frequent international traveler from the South Bay, I can't afford passport delays. Fast Passport Center has been my go-to for years. Professional every time.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "tor-1",
      name: "Torrance Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2510 Carson St",
      city: "Torrance",
      state: "CA",
      zip: "90503",
      phone: "(310) 320-5394",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from downtown Torrance",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Acceptance hours are limited. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "tor-2",
      name: "Redondo Beach Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1201 S Catalina Ave",
      city: "Redondo Beach",
      state: "CA",
      zip: "90277",
      phone: "(310) 376-3376",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "2.5 miles from Redondo Beach Pier",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Busy location — book appointments 2+ weeks in advance. Limited weekend hours.",
      icon: "ri-mail-line",
    },
    {
      id: "tor-3",
      name: "Hermosa Beach Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "565 Pier Ave",
      city: "Hermosa Beach",
      state: "CA",
      zip: "90254",
      phone: "(310) 372-1657",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.3 miles from Hermosa Beach Pier",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular location for beach city residents. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "tor-4",
      name: "Manhattan Beach Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1111 N Valley Dr",
      city: "Manhattan Beach",
      state: "CA",
      zip: "90266",
      phone: "(310) 546-7281",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.8 miles from Manhattan Beach Pier",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for beach city residents. Photo services available. Book ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "tor-5",
      name: "Carson Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "21350 Avalon Blvd",
      city: "Carson",
      state: "CA",
      zip: "90745",
      phone: "(310) 513-0862",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "1.2 miles from Cal State Dominguez Hills",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Carson and Lakewood residents. Limited passport hours — call to confirm.",
      icon: "ri-mail-line",
    },
    {
      id: "tor-6",
      name: "Long Beach Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2300 Redondo Ave",
      city: "Long Beach",
      state: "CA",
      zip: "90815",
      phone: "(562) 596-5572",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "8 miles from Torrance",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Major facility for South Bay and Long Beach residents. Appointments recommended.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — downtown LA", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
    { feature: "South Bay Coverage", agency: "No South Bay coastal expertise", fpc: "Beach cities, aerospace corridor, and South Bay community travel expertise" },
    { feature: "Multi-Person Processing", agency: "Separate appointments per person", fpc: "Handle families and groups in parallel" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "South Bay photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at South Bay acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many South Bay applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the LA regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many South Bay families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "South Bay travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Torrance." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Torrance address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Torrance before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Torrance for travelers with urgent international flights. Our registered couriers achieve 24–48 hour turnaround for most South Bay travelers." },
    { q: "How fast can I renew my passport in Torrance?", a: "Fast Passport Center offers fast passport renewal in Torrance in as little as 24–48 hours through our emergency courier service." },
    { q: "What if my passport expires before my cruise?", a: "If your passport expires before your cruise, contact Fast Passport Center immediately. We can expedite your passport renewal in Torrance in 24–48 hours so you don't miss your departure." },
    { q: "Can I get a same-day passport appointment near Torrance?", a: "Same-day passport appointments are only available at the LA Regional Passport Agency for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Torrance residents." },
    { q: "Can I expedite a child passport in Torrance?", a: "Yes. Fast Passport Center expedites child passports in Torrance for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Torrance with DS-64 form guidance. Our emergency courier service can get your replacement passport in 24–48 hours." },
    {
      q: "How fast can I get a passport in Torrance?",
      a: "With Fast Passport Center's emergency courier service, Torrance travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays.",
    },
    {
      q: "Do I need to visit the Los Angeles Passport Agency downtown?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change.",
    },
    {
      q: "Where are the closest passport acceptance facilities in Torrance?",
      a: "Popular options include the Torrance Main Post Office (90503), Redondo Beach Post Office (90277), Hermosa Beach Post Office (90254), and Manhattan Beach Post Office (90266). All require appointments. We help you find the most convenient location and schedule your visit.",
    },
    {
      q: "Can I get a same-day passport in Torrance?",
      a: "Same-day passports are only available at the LA Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option.",
    },
    {
      q: "What if I live in the beach cities or Palos Verdes?",
      a: "We serve all South Bay communities including Redondo Beach, Hermosa Beach, Manhattan Beach, Palos Verdes, Carson, and Lakewood. Our overnight shipping labels work from any UPS location, so your location within the South Bay doesn't affect processing speed.",
    },
    {
      q: "How much does expedited passport service cost in Torrance?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline.",
    },
    {
      q: "Do you help with passport photos in Torrance?",
      a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and FedEx locations in Torrance and the South Bay offer passport photo services. We review your photos before submission to ensure they meet State Department standards.",
    },
    {
      q: "What documents do I need for a first-time passport in Torrance?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk.",
    },
    {
      q: "Can you help with a child's passport in Torrance?",
      a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk South Bay families through the entire process and help schedule acceptance clerk appointments.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Torrance", address: "20020 Hawthorne Blvd, Torrance, CA 90503", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Redondo Beach", address: "1900 S Pacific Coast Hwy, Redondo Beach, CA 90277", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Torrance", address: "23150 Hawthorne Blvd, Torrance, CA 90505", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "UPS Store — Manhattan Beach", address: "1140 Highland Ave, Manhattan Beach, CA 90266", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Carson", address: "22015 Avalon Blvd, Carson, CA 90745", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Hermosa Beach", address: "601 Pier Ave, Hermosa Beach, CA 90254", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Postal Annex — Torrance", address: "24422 Hawthorne Blvd, Torrance, CA 90505", hours: "Mon–Fri: 9AM–6PM, Sat: 9AM–4PM" },
    { name: "Costco — Hawthorne", address: "1250 W Rosecrans Ave, Hawthorne, CA 90250", hours: "Mon–Fri: 10AM–8:30PM, Sat–Sun: 9:30AM–6PM" },
  ],
  dmvLocations: [
    {
      name: "DMV — Torrance",
      address: "1785 W 220th St, Torrance, CA 90501",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID and driver's license renewals. Appointment required.",
      featured: true,
    },
    {
      name: "DMV — Compton",
      address: "1901 S Alameda St, Compton, CA 90220",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Passport-ready photo services available. Online appointment booking.",
      featured: false,
    },
    {
      name: "DMV — Long Beach",
      address: "3700 E South St, Long Beach, CA 90805",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID processing. Walk-ins limited — book online first.",
      featured: false,
    },
    {
      name: "DMV — Norwalk",
      address: "12400 Imperial Hwy, Norwalk, CA 90650",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Full-service mega center. Longest hours, most services.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "LAX",
      name: "Los Angeles International Airport",
      distance: "12 miles from Torrance",
      address: "1 World Way, Los Angeles, CA 90045",
      note: "The primary international airport for South Bay travelers. Major hub for international flights to Asia, Europe, and Latin America.",
      flightUrl: "https://www.flylax.com/",
      flightLabel: "LAX Flight Info",
      featured: true,
    },
    {
      code: "LGB",
      name: "Long Beach Airport",
      distance: "15 miles from Torrance",
      address: "4100 Donald Douglas Dr, Long Beach, CA 90808",
      note: "Convenient for South Bay residents. Domestic flights with some international connections. Less crowded than LAX.",
      flightUrl: "https://www.longbeach.gov/lgb/",
      flightLabel: "Long Beach Airport",
      featured: false,
    },
    {
      code: "BUR",
      name: "Hollywood Burbank Airport",
      distance: "30 miles from Torrance",
      address: "2627 N Hollywood Way, Burbank, CA 91505",
      note: "Domestic flights with some international connections. Good alternative for Valley-bound travelers.",
      flightUrl: "https://www.hollywoodburbankairport.com/",
      flightLabel: "Burbank Airport",
      featured: false,
    },
  ],
  vitalRecordsLinks: [
    {
      label: "California Dept. of Public Health (Vital Records)",
      description: "State-issued birth and death certificates for all California counties",
      url: "https://www.cdph.ca.gov/Programs/CHSI/Pages/Vital-Records.aspx",
    },
    {
      label: "LA County Registrar-Recorder (Birth, Death, Marriage Records)",
      description: "Birth certificates, death certificates, and marriage records for Los Angeles County",
      url: "https://www.lavote.gov/records/",
    },
    {
      label: "LA County Superior Court (Divorce Records)",
      description: "Divorce decrees and court records for Los Angeles County",
      url: "https://www.lacourt.org/",
    },
  ],
};